// lockpick.ts — FateTide lockpicking minigame (canvas + server validation), carved out of
// includes/js/lockpick_minigame.js and TS-ported for Phase 2a of plans/bundler-script-load-order-phase2.md
// — a Wave-2a fold into post-template-tail.
//
// Pick arc = the whole ring MINUS the knife wedge at ~5–7 o'clock (clock 150–210), delivered by the
// server as extended 210°–510°. Bow = pick; stem = knife. The extended ruler seams inside that wedge
// on purpose — see clockToExtended() and lockpick_sweet_spot_inc.php's header.
//
// SKYRIM-STYLE MECHANICS (2026-07-02 rework): the cylinder's max rotation is the distance signal —
// inside the effective sweet spot (tension_zone_*) the lock turns fully and opens; outside, it turns
// up to partial_turn_max, fading over partial_falloff_deg. Pushing past the jam point shakes the pick
// and runs a strain timer (lockpick_break_seconds); expiry snaps the pick —
// lockpick_break_inc.php consumes one Lockpicking Tools and the session continues until unlock, exit,
// or out of picks. Unlock happens automatically at full turn (no blind submit); the server still
// validates the angle in lockpick_result_inc.php. Interactive SFX come from cfg.sfx (cue → CDN url,
// null-tolerant), volume slaved to the soundboard's effect channel.
//
// MODULE-SCOPE SAFE: the original was an IIFE, so escapeHtml / distPointToSegmentSq / LockpickSfx / the
// FateTideLockpickMinigame class are all module-private here too. At eval only defs + the one window attach
// (window.openLockpickMinigame); the class is instantiated only inside that function at user-interaction
// time. Deps (BASE_URL (core-helpers); loadPostFunctions / refreshSidePanel / closeSideInfoPanel /
// forceOpenSideInfoPanel — typeof-guarded) are all reached at runtime → clean 2a fold.
//
// ⛔ DIFFICULTY IS SERVER-SIDE AND PER-CASTER (2026-07-30). Both partial_falloff_deg and
// lockpick_break_seconds arrive from lockpick_init_inc.php already keyed on the caster's Locks tier
// MINUS the lock's tier (lockpick_skill_edge). Untrained on a Godlike lock that means an 8° feel halo
// and a 0.30s pick, versus 210°/3.6s for a master on a Minor lock — the SAME code path, different
// numbers. Do NOT reintroduce hardcoded difficulty here, and treat the `?? 0.78 / ?? 135` defaults in
// the constructor as last-resort guards for a malformed payload, never as the real values: they are
// the old flat rangefinder that made every lock equally easy to find.
//
// PUBLIC GLOBAL (window-attached): openLockpickMinigame — invoked by table_item_object_inc.php onclick.

function escapeHtml(s: unknown): string {
    const d = document.createElement('div');
    d.textContent = s == null ? '' : String(s);
    return d.innerHTML;
}

function distPointToSegmentSq(px: number, py: number, x1: number, y1: number, x2: number, y2: number): number {
    const dx = x2 - x1;
    const dy = y2 - y1;
    const lenSq = dx * dx + dy * dy;
    if (lenSq < 1e-8) {
        const ux = px - x1;
        const uy = py - y1;
        return ux * ux + uy * uy;
    }
    let t = ((px - x1) * dx + (py - y1) * dy) / lenSq;
    t = Math.max(0, Math.min(1, t));
    const nx = x1 + t * dx;
    const ny = y1 + t * dy;
    const u = px - nx;
    const v = py - ny;
    return u * u + v * v;
}

function lockpickSidelogContext(): { roll_private: 0 | 1 | 2; campaign_id: string; encounter_id: string; caster_id: string } {
    const sl = ftNormalizeEncounterContext(getSidelogContext() ?? {});
    return {
        roll_private: sl.roll_private,
        campaign_id: sl.campaign_id,
        encounter_id: sl.encounter_id,
        caster_id: String(window.ftGetActiveCasterId?.() ?? '').trim(),
    };
}

/**
 * CLIENT TRUTH (Lane L5 S2/S3, 2026-09-26): the attempt is a SESSION object owned by the websocket bundle's minigame-truth
 * adapter (window.ftLockpickBegin / Break / Cancel / Result → options/game/actions/minigame_truth_commit_inc.php). The retired
 * lockpick_{init,result,break,cancel}_inc.php server token is gone; `token` below now holds that session.
 */
function lpCancel(session: unknown, reason: string, keepalive = false): void {
    try { void window.ftLockpickCancel?.(session, reason, keepalive)?.catch(() => { /* ignore */ }); } catch { /* ignore */ }
}

/**
 * ── side-info drawer OWNERSHIP while a minigame is live ──────────────────────────────────────
 *
 * #div_refresh_side_info is a SHARED host. Several AUTOMATIC (no-user-input) writers repaint it:
 * the websocket open-side-info subscribe (ftRerenderOpenSideInfo, fired by caster_update /
 * caster_update_delta / encounter_update / encounter_delta), the GM start-of-turn auto-open
 * (start-turn-gm.ts), and the generic post-action refreshSidePanel() (template.js). The minigame
 * mounts its canvas INTO that host — so any of those landing mid-attempt replaces the canvas, and
 * loop()'s orphan check reads the detached canvas as "panel replaced under us" and CANCELS the
 * attempt server-side. That is exactly the owner-reported 2026-07-24 bug: start lockpicking, then
 * ~1–2s later (the post-flush recompile + WS push window opened by our OWN init: it applies the
 * lockpicking status and spends AP) the attempt cancels itself with no user input.
 *
 * So while a minigame is live we CLAIM the drawer: the open-side-info registry is cleared (nothing
 * is "the open card" any more) and a window flag tells the automatic writers to skip. Deliberate
 * user navigation (openItemInfo / closing the panel) is NOT blocked — leaving still ends the
 * attempt, same as before.
 */
// Whose claim is currently live. Opening a SECOND lock replaces the panel, which orphans the
// first minigame — its loop() then tears down a frame later. Without this token that late
// teardown would clear the claim the SECOND minigame just made, leaving the new attempt exposed
// to exactly the repaint this guard exists to stop. Release is therefore owner-scoped.
let sideInfoMinigameOwner: object | null = null;

function claimSideInfoForMinigame(host: HTMLElement, owner: object): void {
    sideInfoMinigameOwner = owner;
    window.__ftSideInfoMinigameActive = true;
    try { host.dataset.ftSideinfoOwner = 'lockpick'; } catch { /* non-fatal */ }
    // Drop the registered open card, or a websocket repaint that touches the lock (or the caster
    // carrying it) would paint that card straight back over the canvas.
    try { window.ftClearOpenSideInfo?.(); } catch { /* non-fatal */ }
}

/** Does `owner` still hold the drawer? False once a newer minigame has claimed it. */
function ownsSideInfo(owner: object): boolean {
    return sideInfoMinigameOwner === owner;
}

/** Release the claim (teardown). Must run BEFORE the teardown's own refreshSidePanel/loadPostFunctions. */
function releaseSideInfoForMinigame(owner: object): void {
    if (sideInfoMinigameOwner !== owner) return; // superseded by a newer minigame — not ours to clear
    sideInfoMinigameOwner = null;
    window.__ftSideInfoMinigameActive = false;
    const host = document.getElementById('div_refresh_side_info');
    if (host) { try { delete host.dataset.ftSideinfoOwner; } catch { /* non-fatal */ } }
}

/**
 * Interactive minigame SFX. One-shots via throwaway Audio elements (created inside user-gesture
 * handlers, so autoplay is unlocked); the strain rattle is a dedicated loop element. Volume is
 * slaved to the soundboard's first effect channel so the user's mixer settings apply; any missing
 * cue url (cfg.sfx[cue] null — not ingested yet) silently degrades.
 */
class LockpickSfx {
    private urls: Record<string, string | null>;
    private strainEl: HTMLAudioElement | null = null;
    private lastTickAt = 0;

    constructor(urls: unknown) {
        this.urls = urls && typeof urls === 'object' ? (urls as Record<string, string | null>) : {};
    }

    private masterVolume(): number {
        const ref = document.getElementById('audio_player_effect_1') as HTMLAudioElement | null;
        if (!ref) return 0.8;
        if (ref.muted || ref.volume <= 0) return 0;
        return ref.volume;
    }

    private oneShot(cue: string, volMul: number, rate: number): void {
        const url = this.urls[cue];
        const vol = this.masterVolume() * volMul;
        if (!url || vol <= 0) return;
        try {
            const a = new Audio(url);
            a.volume = Math.min(1, vol);
            a.playbackRate = rate;
            window.ftRegisterSiteAudioTransient?.(a);
            void a.play().catch(() => { /* autoplay/abort — ignore */ });
        } catch { /* ignore */ }
    }

    /** Throttled tick while the pick sweeps (playbackRate jitter fakes variants). */
    tick(): void {
        const now = performance.now();
        if (now - this.lastTickAt < 90) return;
        this.lastTickAt = now;
        this.oneShot('pick_move', 0.45, 0.88 + Math.random() * 0.28);
    }

    tension(): void { this.oneShot('tension', 0.7, 0.95 + Math.random() * 0.1); }
    jam(): void { this.oneShot('jam', 0.8, 0.9 + Math.random() * 0.2); }
    snap(): void { this.strain(false, 0); this.oneShot('pick_break', 1, 1); }
    unlock(): void { this.strain(false, 0); this.oneShot('unlock', 1, 1); }

    /** Looping rattle while jammed; intensity 0..1 (strain progress) drives volume + rate. */
    strain(on: boolean, intensity: number): void {
        if (!on) {
            if (this.strainEl && !this.strainEl.paused) this.strainEl.pause();
            return;
        }
        const url = this.urls['strain'];
        const vol = this.masterVolume();
        if (!url || vol <= 0) return;
        if (!this.strainEl) {
            try {
                this.strainEl = new Audio(url);
                this.strainEl.loop = true;
            } catch { return; }
        }
        const t = Math.min(1, Math.max(0, intensity));
        this.strainEl.volume = Math.min(1, vol * (0.35 + 0.65 * t));
        this.strainEl.playbackRate = 0.9 + 0.35 * t;
        if (this.strainEl.paused) {
            window.ftRegisterSiteAudioTransient?.(this.strainEl);
            void this.strainEl.play().catch(() => { /* ignore */ });
        }
    }

    dispose(): void {
        if (this.strainEl) {
            this.strainEl.pause();
            this.strainEl.src = '';
            this.strainEl = null;
        }
    }
}

/**
 * ── PICK WEAR (2026-08-03) ───────────────────────────────────────────────────────────────────
 * Seconds of PERMANENT pick damage per second of straining.
 *
 * WHY IT EXISTS: strainTime decays to zero the moment you ease off, so a patient player could lean
 * on the dagger at every position on the ring, read how far the cylinder budged, release before the
 * snap, and repeat — a complete distance map for the price of wall-clock time. That is the same
 * zero-cost rangefinder the 2026-07-30 difficulty rework was written to kill (see the jamTolerance
 * block in loop()); the rework closed the micro-drag route and left this one open. Wear closes it:
 * relief is real but never complete, because the meter now falls back to a rising floor.
 *
 * ⚠ FLAT accrual per second of straining, NOT a fraction of the strain built. A fraction-of-strain
 * model asymptotes — each probe adds less than the last, so the floor creeps toward the ceiling and
 * never reaches it, leaving probing unbounded while looking like it works. Flat accrual makes the
 * budget finite.
 *
 * ⚠ Do NOT re-derive this rate as "budget / cost-per-probe". Only the FIRST push climbs from zero;
 * every later one re-climbs the shrinking gap to the floor, so probe count converges geometrically
 * (~log/log(1 − rate)) and the naive estimate is off by 3x — 0.35 "feels" like a third of a pick per
 * probe but actually allows twelve. 0.75 was established empirically by
 * tools/test/lockpick-wear-budget-check.mjs: 3–5 probes per pick across the whole skill ladder,
 * 2–3 at the hopeless end. Re-run that script before changing it.
 */
const LOCKPICK_WEAR_RATE = 0.75;

/**
 * ── BAKED TOOL SPRITES (2026-08-03) ──────────────────────────────────────────────────────────
 * The dagger and pick used to be drawn as vector primitives. That was fine against the old flat
 * brass disc, but the face is now the lock's own photographic render and the vector tools read as
 * placeholder next to it. These are 3D bakes: the dagger from a purchased 11.8 MB GLB, the pick
 * modelled to match, both rendered orthographically under one studio environment so they read as a
 * set. See tools/build/lockpick-tool-bake.mjs.
 *
 * Baked rather than rendered live because the panel is 320px inside a ≤360px drawer: a sprite keeps
 * the whole fidelity of the source asset for ~124 KB, needs no WebGL context, and — the real reason
 * — leaves the pick/dagger HIT-TESTING untouched. That geometry is 2D canvas math with a long history
 * of subtle bugs (see this file's header); swapping how the tools are PAINTED must not disturb it.
 *
 * Module-scope so both are fetched once per page rather than per attempt. Every draw is guarded on
 * `complete && naturalWidth > 0` and falls back to the original vector art — a slow image must never
 * leave the player with an invisible tool they are supposed to grab.
 */
const LOCKPICK_TOOL_TIP_FRAC = 0.02; // transparent padding the bake leaves above the tool's tip
let lockpickDaggerSprite: HTMLImageElement | null = null;
let lockpickPickSprite: HTMLImageElement | null = null;

function ensureLockpickToolSprites(): void {
    if (lockpickDaggerSprite) return;
    lockpickDaggerSprite = new Image();
    lockpickDaggerSprite.src = 'https://cdn.fatetide.com/images/lockpick/dagger.png';
    lockpickPickSprite = new Image();
    lockpickPickSprite.src = 'https://cdn.fatetide.com/images/lockpick/pick.png';
}

/** Decoded and safe to draw? An Image that errored reports complete=true with naturalWidth 0. */
function spriteReady(img: HTMLImageElement | null): img is HTMLImageElement {
    return !!img && img.complete && img.naturalWidth > 0;
}

interface LockpickConfig {
    arc_min: number;
    arc_max: number;
    token: unknown;
    integrated_lock_face?: unknown;
    lockpick_break_seconds?: number;
    dead_start?: number;
    dead_end?: number;
    tension_zone_lo?: number;
    tension_zone_hi?: number;
    partial_turn_max?: number;
    partial_falloff_deg?: number;
    picks_remaining?: number;
    picks_consumable?: unknown;
    sfx?: unknown;
}

class FateTideLockpickMinigame {
    canvas: HTMLCanvasElement;
    ctx: CanvasRenderingContext2D;
    cfg: LockpickConfig;
    wrapEl: HTMLElement;
    arcMin: number;
    arcMax: number;
    token: unknown;
    integrated: boolean;
    breakSeconds: number;
    dpr: number;
    w: number;
    h: number;
    cx: number;
    cy: number;
    faceR: number;
    lockpickPivotLocalY: number;
    knifeTopLocalY: number;
    knifeGuardLocalY: number;
    knifeBottomLocalY: number;
    deadStart: number;
    deadEnd: number;
    zoneCenter: number;
    zoneHalf: number;
    partialMax: number;
    falloffDeg: number;
    picksRemaining: number;
    picksConsumable: boolean;
    sfx: LockpickSfx;
    knifeHoverActive: boolean;
    pickAngle: number;
    draggingPick: boolean;
    torqueInput: number;
    draggingTorque: boolean;
    _torquePrevAng: number | null;
    _activePointerId: number | null;
    _onWindowBlur: () => void;
    submitting: boolean;
    breaking: boolean;
    exiting: boolean;
    finished: boolean;
    strainTime: number;
    /** Permanent damage to the CURRENT pick, in the same seconds as strainTime. See LOCKPICK_WEAR_RATE. */
    pickWear: number;
    _jamKnocked: boolean;
    /**
     * Has the cylinder EVER been forced past its jam point this session? Sent to the server as
     * `no_strain` and it is half of the flawless-pick test (lockpick_result_inc.php owns the other
     * half, the server-side break count). Deliberately sticky: it survives a pick snapping, a
     * non-consumable lock's free slip, and every relief, because a run that jammed once is not
     * flawless no matter what happened afterwards.
     */
    everStrained: boolean;
    _tensionCreaked: boolean;
    vibration: number;
    cylinderTurn: number;
    _frameKnobJitter: number;
    _lastFrameTs: number;
    pickHitPx: number;
    knifeHitPx: number;
    KNOB_VISUAL: number;
    pickBladeLen: number;
    _introStart: number;
    _loop: () => void;

    constructor(canvas: HTMLCanvasElement, cfg: LockpickConfig, wrapEl: HTMLElement) {
        this.canvas = canvas;
        this.ctx = canvas.getContext('2d') as CanvasRenderingContext2D;
        this.cfg = cfg;
        this.wrapEl = wrapEl;
        // Defaulted, unlike before: arcMin is now the SEAM clockToExtended() cuts at, so a missing
        // arc_min would turn every pointer angle into NaN and freeze the pick, not just skew the
        // clamp. Values mirror lockpick_sweet_spot_inc.php; the server always sends them.
        //
        // Validated as a PAIR, and with Number.isFinite rather than typeof: `typeof NaN` is
        // 'number', and defaulting the two bounds independently would let a partial or mixed
        // old/new payload assemble an incoherent ruler (e.g. arc_min 210 with a stale arc_max
        // 405) — a half-fixed arc is worse than the documented default.
        const arcOk = Number.isFinite(cfg.arc_min) && Number.isFinite(cfg.arc_max)
            && (cfg.arc_max as number) - (cfg.arc_min as number) > 0;
        this.arcMin = arcOk ? (cfg.arc_min as number) : 210;
        this.arcMax = arcOk ? (cfg.arc_max as number) : 510;
        this.token = cfg.token;
        this.integrated = !!cfg.integrated_lock_face;
        // ⚠ `x || 1` does NOT sanitise this: negative numbers are truthy, so a malformed payload
        // could land a negative threshold — which makes `strainTime >= breakSeconds` true on the
        // first frame (a pick that snaps on contact, forever) and lets pickWear's Math.min cap go
        // negative. The server clamps its own modifier at -90% so this is unreachable from sane
        // data, but the arc bounds above are validated defensively for exactly the same reason and
        // this deserves the same treatment.
        this.breakSeconds = Number.isFinite(cfg.lockpick_break_seconds) && (cfg.lockpick_break_seconds as number) > 0
            ? (cfg.lockpick_break_seconds as number)
            : 1;

        // ── HiDPI: back the canvas with device pixels, keep every coordinate in CSS pixels ──
        // The markup ships width="320" height="380" and the stylesheet scales it to fit the
        // ≤360px drawer, so on a 2× display 320 real pixels were being stretched across ~680 —
        // the whole face was resampled before the art was even the problem. Enlarge the backing
        // store by dpr and pre-scale the context once per frame (see loop()); w/h stay the CSS
        // design size, so the geometry, hit-testing and HUD layout below are untouched.
        //
        // ⚠ w/h are the LOGICAL size from here on, NOT canvas.width/height. toCanvasXY() divides
        // by these to map pointer coords into draw space — reading the backing store there would
        // re-introduce the dpr factor and put every hitbox at half the pointer's real distance.
        const cssW = canvas.width || 320;
        const cssH = canvas.height || 380;
        // Capped at 3: past that the buffer grows quadratically for no visible gain on a 320px face.
        this.dpr = Math.min(3, Math.max(1, window.devicePixelRatio || 1));
        canvas.width = Math.round(cssW * this.dpr);
        canvas.height = Math.round(cssH * this.dpr);
        // Width only. The stylesheet's `max-width:100%` still shrinks it in a cramped drawer and
        // its `height:auto` then follows the attribute aspect ratio — pinning an inline height
        // here would win over that rule and squash the face whenever the panel is narrower.
        canvas.style.width = cssW + 'px';
        this.w = cssW;
        this.h = cssH;
        this.cx = this.w / 2;
        this.cy = this.h * 0.42;
        this.faceR = Math.min(this.w, this.h) * 0.28;

        const g = this.getStemGeometry();
        this.lockpickPivotLocalY = g.bowCy + g.bowR * 0.22;
        this.knifeTopLocalY = g.mergeY - g.stemH * 0.12;
        // The tension dagger runs from the lower keyhole slot out PAST the face edge: the grip has
        // to read as a separate tool you grab and twist (owner: "the dagger is missing"), and the
        // longer body is a far bigger hit target than the old stub that stopped inside the brass.
        this.knifeGuardLocalY = this.faceR * 0.66;
        this.knifeBottomLocalY = g.mergeY + g.stemH + this.faceR * 0.95;

        this.deadStart = typeof cfg.dead_start === 'number' ? cfg.dead_start : 150;
        this.deadEnd = typeof cfg.dead_end === 'number' ? cfg.dead_end : 210;

        const lo = typeof cfg.tension_zone_lo === 'number' ? cfg.tension_zone_lo : 260;
        const hi = typeof cfg.tension_zone_hi === 'number' ? cfg.tension_zone_hi : 280;
        this.zoneCenter = (lo + hi) / 2;
        this.zoneHalf = Math.max(0.5, (hi - lo) / 2);
        this.partialMax = typeof cfg.partial_turn_max === 'number' ? cfg.partial_turn_max : 0.78;
        // Fallbacks mirror lockpick_sweet_spot_inc.php's constants; the server always sends both.
        this.falloffDeg = typeof cfg.partial_falloff_deg === 'number' ? cfg.partial_falloff_deg : 135;
        this.picksRemaining = typeof cfg.picks_remaining === 'number' ? cfg.picks_remaining : 0;
        this.picksConsumable = !!cfg.picks_consumable;
        this.sfx = new LockpickSfx(cfg.sfx);

        this.knifeHoverActive = false;
        // Start at 12 o'clock, Skyrim-style — the same spawn handleBreak() uses for a fresh pick.
        // (The old arc midpoint was 270 = 9 o'clock; before the render fix below that DREW at 6
        // o'clock, i.e. squarely on top of the dagger, which is why the dagger looked missing.)
        this.pickAngle = Math.max(this.arcMin, Math.min(this.arcMax, 360));
        this.draggingPick = false;
        this.torqueInput = 0;
        this.draggingTorque = false;
        this._torquePrevAng = null;
        this._activePointerId = null;
        this.submitting = false;
        this.breaking = false;
        this.exiting = false;
        this.finished = false;
        this.strainTime = 0;
        this.pickWear = 0;
        this._jamKnocked = false;
        this.everStrained = false;
        this._tensionCreaked = false;
        this.vibration = 0;
        this.cylinderTurn = 0;
        this._frameKnobJitter = 0;
        this._lastFrameTs = 0;
        this.pickHitPx = 20;
        this.knifeHitPx = 24;

        // Radians of face rotation at a full cylinder turn. Was 0.35 (~20°) — too subtle now
        // that HOW FAR IT TURNS is the distance signal; ~52° reads like Skyrim's quarter-turn.
        this.KNOB_VISUAL = 0.9;
        this.pickBladeLen = this.faceR * 0.68;
        this._introStart = typeof performance !== 'undefined' ? performance.now() : 0;
        // Kick the fetch at construction so the sprites are usually decoded by the first frame;
        // the draw guards cover the case where they are not.
        ensureLockpickToolSprites();

        canvas.addEventListener('pointerdown', this.onPointerDown.bind(this));
        canvas.addEventListener('pointermove', this.onPointerMove.bind(this));
        canvas.addEventListener('pointerup', this.onPointerUp.bind(this));
        canvas.addEventListener('pointercancel', this.onPointerUp.bind(this));
        canvas.addEventListener('pointerleave', this.onPointerLeave.bind(this));
        // Capture can vanish without pointerup (element shuffle, alt-tab, OS gestures) — a stuck
        // draggingTorque would keep straining unattended until the pick snapped.
        canvas.addEventListener('lostpointercapture', this.onLostPointerCapture.bind(this));
        this._onWindowBlur = () => this.releaseDrags();
        window.addEventListener('blur', this._onWindowBlur);
        canvas.style.touchAction = 'none';

        const btnExit = wrapEl.querySelector('#lockpick_btn_exit');
        if (btnExit) {
            btnExit.addEventListener('click', () => this.exitMinigame());
        }

        this._loop = this.loop.bind(this);
        requestAnimationFrame(this._loop);
    }

    getStemGeometry() {
        const fr = this.faceR;
        const bowR = fr * 0.1;
        const stemW = fr * 0.072;
        const stemH = fr * 0.2;
        const bowCy = -fr * 0.05;
        const mergeY = bowCy + bowR * 0.92;
        return { bowR, stemW, stemH, bowCy, mergeY };
    }

    knobRad() {
        return this.cylinderTurn * this.KNOB_VISUAL + this._frameKnobJitter;
    }

    /** Knob rotation without render jitter (aim + hit testing). */
    knobRadStable() {
        return this.cylinderTurn * this.KNOB_VISUAL;
    }

    /**
     * Face-local (lock body) point → canvas world; k = knob angle (rad).
     * MUST match drawFace()'s translate(cx,cy) + rotate(k) exactly, i.e. canvas's
     * (lx,ly) → (lx·cos k − ly·sin k, lx·sin k + ly·cos k). It previously used +ly·s / −lx·s,
     * which is rotation by −k — so every hitbox swung the OPPOSITE way from the drawn art as
     * the cylinder turned (~80° adrift at a full partial turn, making the dagger un-regrabbable).
     */
    localPointToWorld(lx: number, ly: number, k: number) {
        const c = Math.cos(k);
        const s = Math.sin(k);
        return {
            x: this.cx + lx * c - ly * s,
            y: this.cy + lx * s + ly * c,
        };
    }

    /**
     * Pointer client coords → canvas DRAW-SPACE (CSS-pixel) coords. The stylesheet gives
     * .lockpick-canvas `max-width:100%; height:auto` and .side_info_panel caps at 360px, so in a
     * cramped drawer the 320×380 face is displayed smaller — raw `clientX - rect.left` is then off
     * by the display scale, and the error grows with distance from the pivot (worse than the 20px
     * pick radius out at the blade tip). Everything below hit-tests in draw space, so convert once
     * here.
     *
     * ⚠ Scales by this.w/this.h (the CSS design size), NOT canvas.width/height. Since the HiDPI
     * change the backing store is dpr× larger and the context carries a matching pre-scale, so
     * dividing by the backing store would double-count dpr and land every hit test at half the
     * pointer's true offset from the pivot.
     */
    toCanvasXY(clientX: number, clientY: number) {
        const rect = this.canvas.getBoundingClientRect();
        const sx = rect.width > 0 ? this.w / rect.width : 1;
        const sy = rect.height > 0 ? this.h / rect.height : 1;
        return { x: (clientX - rect.left) * sx, y: (clientY - rect.top) * sy };
    }

    smoothstep01(t: number) {
        t = Math.max(0, Math.min(1, t));
        return t * t * (3 - 2 * t);
    }

    introPickAlpha() {
        return this.smoothstep01((performance.now() - this._introStart) / 400);
    }

    introKnifeAlpha() {
        return this.smoothstep01((performance.now() - this._introStart - 120) / 460);
    }

    circularDistDeg(a: number, b: number) {
        const diff = Math.abs(a - b);
        return Math.min(diff, 360 - diff);
    }

    /** 0° = 12 o'clock, CW+; snap out of open (deadStart, deadEnd) knife wedge. */
    clampClockOutsideDeadZone(clockDeg: number) {
        let x = ((clockDeg % 360) + 360) % 360;
        if (x > this.deadStart && x < this.deadEnd) {
            const dLo = this.circularDistDeg(x, this.deadStart);
            const dHi = this.circularDistDeg(x, this.deadEnd);
            x = dLo <= dHi ? this.deadStart : this.deadEnd;
        }
        return x;
    }

    /**
     * Clock → the server's extended ruler. The seam is the arc floor, which the server parks
     * inside the dagger wedge, so every clock angle the pick can legally occupy has an in-range
     * preimage. Derived from arcMin (never hardcoded) so client and server cannot drift.
     *
     * ⚠ This used to cut at a literal 45 while arcMin was 135, which left clock (45,135) — the
     * whole right side of the face — with no representation inside the arc: the clamp below
     * pinned it to 135, so the pick could not be placed on the right at all, and crossing 1:30
     * flung it 90° round to 4:30. See lockpick_sweet_spot_inc.php's header.
     */
    clockToExtended(clockDeg: number) {
        const c = ((clockDeg % 360) + 360) % 360;
        if (c < this.arcMin) {
            return c + 360;
        }
        return c;
    }

    /**
     * The heart of the Skyrim mechanic: how far the cylinder CAN turn for the current pick
     * position. 1 inside the effective sweet spot (full turn → open); outside it fades from
     * partialMax to 0 over falloffDeg past the zone edge — so the jam point is the distance meter.
     * Both angles live on the same extended line, and it is cut inside the dagger wedge, so plain
     * |Δ| IS the distance along the reachable ring — measured the only way the pick can travel.
     */
    maxTurnForPick(): number {
        const dist = Math.abs(this.pickAngle - this.zoneCenter);
        if (dist <= this.zoneHalf) {
            return 1;
        }
        const past = dist - this.zoneHalf;
        return this.partialMax * Math.max(0, 1 - past / this.falloffDeg);
    }

    pointerToPickAngle(clientX: number, clientY: number) {
        const p = this.toCanvasXY(clientX, clientY);
        const pv = this.localPointToWorld(0, this.lockpickPivotLocalY, this.knobRadStable());
        const mx = p.x - pv.x;
        const my = p.y - pv.y;
        let d = (Math.atan2(my, mx) * 180) / Math.PI + 90;
        d = ((d % 360) + 360) % 360;
        d = this.clampClockOutsideDeadZone(d);
        const u = this.clockToExtended(d);
        return Math.max(this.arcMin, Math.min(this.arcMax, u));
    }

    pickSegment(clientX: number, clientY: number) {
        const p = this.toCanvasXY(clientX, clientY);
        const px = p.x;
        const py = p.y;
        const pv = this.localPointToWorld(0, this.lockpickPivotLocalY, this.knobRadStable());
        const k = this.knobRadStable();
        // pickAngle is an EXTENDED clock angle (0 = 12 o'clock, CW+, plus whole turns) — the
        // convention the server, the dead wedge and pointerToPickAngle all use. Rotation is
        // periodic so the extra turn is free here. The old `- 90` drew/hit-tested the pick a
        // quarter turn counter-clockwise of its true angle, parking the default on the dagger.
        const pickRel = (this.pickAngle * Math.PI) / 180 + k;
        const dirX = Math.sin(pickRel);
        const dirY = -Math.cos(pickRel);
        const len = this.pickBladeLen;
        return {
            px,
            py,
            x1: pv.x,
            y1: pv.y,
            x2: pv.x + dirX * len,
            y2: pv.y + dirY * len,
        };
    }

    knifeSegment(clientX: number, clientY: number) {
        const p = this.toCanvasXY(clientX, clientY);
        const px = p.x;
        const py = p.y;
        const k = this.knobRadStable();
        const span = this.knifeBottomLocalY - this.knifeTopLocalY;
        const p0 = this.localPointToWorld(0, this.knifeTopLocalY + span * 0.08, k);
        const p1 = this.localPointToWorld(0, this.knifeBottomLocalY, k);
        return { px, py, x1: p0.x, y1: p0.y, x2: p1.x, y2: p1.y };
    }

    distToPickSq(clientX: number, clientY: number) {
        const seg = this.pickSegment(clientX, clientY);
        return distPointToSegmentSq(seg.px, seg.py, seg.x1, seg.y1, seg.x2, seg.y2);
    }

    distToKnifeSq(clientX: number, clientY: number) {
        const seg = this.knifeSegment(clientX, clientY);
        return distPointToSegmentSq(seg.px, seg.py, seg.x1, seg.y1, seg.x2, seg.y2);
    }

    isNearPick(clientX: number, clientY: number, thresholdPx: number) {
        return this.distToPickSq(clientX, clientY) <= thresholdPx * thresholdPx;
    }

    isNearKnife(clientX: number, clientY: number, thresholdPx: number) {
        return this.distToKnifeSq(clientX, clientY) <= thresholdPx * thresholdPx;
    }

    /** Pick can only re-aim while the cylinder is at rest (it's pinned once tension is on). */
    pickMovable(): boolean {
        return !this.draggingTorque && this.cylinderTurn < 0.04;
    }

    interactionLocked(): boolean {
        return this.submitting || this.breaking || this.exiting || this.finished;
    }

    updateHoverCursor(clientX: number, clientY: number) {
        if (this.interactionLocked()) {
            this.knifeHoverActive = false;
            return;
        }
        // Mirror onPointerDown's nearest-wins arbitration, or the dagger would glow while a click
        // would actually grab the pick.
        const dKnifeSq = this.distToKnifeSq(clientX, clientY);
        const dPickSq = this.distToPickSq(clientX, clientY);
        const knifeHit = dKnifeSq <= this.knifeHitPx * this.knifeHitPx;
        const pickHit = this.pickMovable() && dPickSq <= this.pickHitPx * this.pickHitPx;
        const pickWins = pickHit && (!knifeHit || dPickSq <= dKnifeSq);
        const nearKnife = knifeHit && !pickWins;
        this.knifeHoverActive = nearKnife && !this.draggingTorque;
        if (this.draggingPick || this.draggingTorque) {
            this.canvas.style.cursor = 'grabbing';
        } else if (nearKnife || pickWins) {
            this.canvas.style.cursor = 'grab';
        } else {
            this.canvas.style.cursor = 'default';
        }
    }

    /** Drop any in-flight drag without touching torque (it springs back in loop()). */
    releaseDrags() {
        this.draggingPick = false;
        this.draggingTorque = false;
        this._torquePrevAng = null;
        this._activePointerId = null;
        this.canvas.style.cursor = 'default';
    }

    onLostPointerCapture(e: PointerEvent) {
        if (this._activePointerId !== null && e.pointerId === this._activePointerId) {
            this.releaseDrags();
        }
    }

    onPointerDown(e: PointerEvent) {
        if (this.interactionLocked()) return;
        // One pointer owns the gesture — a second finger must not hijack or cancel it.
        if (this._activePointerId !== null) return;
        // NEAREST tool wins. The knife used to win unconditionally (tested first, and with the
        // LARGER radius) — so wherever the two overlapped, the pick could never be grabbed at all.
        // pickMovable() still pins the pick while torque is on, so the knife keeps owning the
        // gesture once the cylinder is under tension.
        const dKnifeSq = this.distToKnifeSq(e.clientX, e.clientY);
        const dPickSq = this.distToPickSq(e.clientX, e.clientY);
        const knifeHit = dKnifeSq <= this.knifeHitPx * this.knifeHitPx;
        const pickHit = this.pickMovable() && dPickSq <= this.pickHitPx * this.pickHitPx;

        if (pickHit && (!knifeHit || dPickSq <= dKnifeSq)) {
            this.draggingPick = true;
            this._activePointerId = e.pointerId;
            this.pickAngle = this.pointerToPickAngle(e.clientX, e.clientY);
            this.canvas.style.cursor = 'grabbing';
            try {
                (e.target as Element).setPointerCapture(e.pointerId);
            } catch (err) {
                /* ignore */
            }
        } else if (knifeHit) {
            this.draggingTorque = true;
            this._torquePrevAng = null;
            this._activePointerId = e.pointerId;
            if (!this._tensionCreaked) {
                this._tensionCreaked = true;
                this.sfx.tension();
            }
            try {
                (e.target as Element).setPointerCapture(e.pointerId);
            } catch (err) {
                /* ignore */
            }
            this.canvas.style.cursor = 'grabbing';
        }
    }

    onPointerMove(e: PointerEvent) {
        if (this.interactionLocked()) return;
        // While a drag is live, only its owning pointer may steer it.
        if (this._activePointerId !== null && e.pointerId !== this._activePointerId) return;

        if (this.draggingPick) {
            const prev = this.pickAngle;
            this.pickAngle = this.pointerToPickAngle(e.clientX, e.clientY);
            if (Math.abs(this.pickAngle - prev) > 1.2) {
                this.sfx.tick();
            }
        } else if (this.draggingTorque) {
            // Torque input is ONE signed measurement: how far the pointer swept AROUND the face,
            // clockwise-positive. Both directions count — winding the dagger back counter-clockwise
            // EASES the tension. It used to only ever accumulate (`movementX > 0`, `d > 0`), a
            // one-way ratchet: once over-torqued into a jam, the only escape was releasing.
            //
            // ⚠ Do NOT re-add a `movementX` term alongside this. The dagger hangs DOWNWARD, so at
            // the grip a clockwise sweep moves the pointer LEFT: movementX < 0 while d > 0. Summing
            // them gives inc ≈ d·(0.24 − 0.004r), which stalls at r≈60px and REVERSES beyond it —
            // i.e. over most of the grip, twisting clockwise would have released tension. (It was
            // latent before only because both terms were clamped to positive.) movementX is also
            // in CSS pixels, so the cancellation radius would drift with the drawer width.
            //
            // Angular delta is scale-invariant, but it explodes near the pivot (a few px of travel
            // is a huge angle), so weight it by radius: inc ∝ actual tangential travel, saturating
            // at TORQUE_REF_R. The cylinder still never turns past 0 the wrong way (floor below);
            // it follows in loop(), capped at maxTurnForPick().
            const TORQUE_GAIN = 0.55;                 // full tension ≈ 105° of sweep at the grip
            const TORQUE_REF_R = this.faceR * 0.8;    // ~mid-grip; nearer the pivot damps down
            let inc = 0;
            const p = this.toCanvasXY(e.clientX, e.clientY);
            const ang = Math.atan2(p.y - this.cy, p.x - this.cx);
            if (this._torquePrevAng !== null) {
                let d = ang - this._torquePrevAng;
                if (d > Math.PI) d -= 2 * Math.PI;
                if (d < -Math.PI) d += 2 * Math.PI;
                // A pointer whipping across the centre can hand us a huge delta in one event.
                d = Math.max(-0.5, Math.min(0.5, d));
                const r = Math.hypot(p.x - this.cx, p.y - this.cy);
                inc = d * Math.min(1, r / TORQUE_REF_R) * TORQUE_GAIN;
            }
            this._torquePrevAng = ang;
            this.torqueInput = Math.max(0, Math.min(1.12, this.torqueInput + inc));
        } else {
            this.updateHoverCursor(e.clientX, e.clientY);
        }
    }

    onPointerUp(e: PointerEvent) {
        // A stray pointer's up/cancel must not end the owning pointer's drag.
        if (this._activePointerId !== null && e.pointerId !== this._activePointerId) return;
        this._activePointerId = null;
        if (this.draggingPick) {
            this.draggingPick = false;
            try {
                (e.target as Element).releasePointerCapture(e.pointerId);
            } catch (err) {
                /* ignore */
            }
            this.updateHoverCursor(e.clientX, e.clientY);
        }
        if (this.draggingTorque) {
            this.draggingTorque = false;
            this._torquePrevAng = null;
            try {
                (e.target as Element).releasePointerCapture(e.pointerId);
            } catch (err) {
                /* ignore */
            }
            // No blind submit — the cylinder springs back in loop(); unlock only ever
            // fires from a full turn inside the sweet spot.
            this.updateHoverCursor(e.clientX, e.clientY);
        }
    }

    onPointerLeave() {
        this.knifeHoverActive = false;
        // Capture normally suppresses pointerleave for the duration of a drag — so a leave
        // arriving while a drag is live means setPointerCapture failed/threw, and neither
        // pointerup on the canvas nor lostpointercapture will ever end it. Release here.
        if (
            this._activePointerId !== null
            && typeof this.canvas.hasPointerCapture === 'function'
            && !this.canvas.hasPointerCapture(this._activePointerId)
        ) {
            this.releaseDrags();
            return;
        }
        if (!this.draggingTorque && !this.draggingPick) {
            this.canvas.style.cursor = 'default';
        }
    }

    /**
     * Strain timer expired: the pick snaps. Server consumes one Lockpicking Tools (when this
     * lock consumes picks) and tells us whether the session survives. Locks that don't list
     * Lockpicking Tools just slip the pick — no server call, no loss.
     */
    async handleBreak() {
        if (this.breaking || this.submitting || this.finished) return;
        const hint = this.wrapEl.querySelector('#lockpick_hint');

        if (!this.picksConsumable) {
            this.sfx.strain(false, 0);
            this.sfx.jam();
            this.torqueInput = 0;
            this.strainTime = 0;
            // A lock that doesn't consume picks costs nothing but the slip — so the wear resets
            // too, or the player would accumulate permanent damage on a pick that is never spent.
            this.pickWear = 0;
            if (hint) {
                hint.textContent = 'The pick slips out — ease off before it jams that hard.';
            }
            return;
        }

        this.breaking = true;
        this.releaseDrags();
        this.sfx.snap();
        this.torqueInput = 0;
        this.strainTime = 0;
        // The snapped pick is spent; whatever replaces it is undamaged.
        this.pickWear = 0;
        this.vibration = 0;
        // Exit races the in-flight break against the same token — hold it until we know
        // whether the session survived.
        const btnExit = this.wrapEl.querySelector('#lockpick_btn_exit') as HTMLButtonElement | null;
        if (btnExit) btnExit.disabled = true;

        try {
            const j = window.ftLockpickBreak ? await window.ftLockpickBreak(this.token) : null;
            if (!j || !j.ok) {
                this.endSession(j?.error === 'attempt_expired' ? 'Session expired.' : 'Lockpicking ended.');
                return;
            }
            this.picksRemaining = j.picksRemaining;
            if (j.sessionOver) {
                this.endSession('Your pick snapped — and that was your last one.');
                return;
            }
            // Fresh pick: spawns at 12 o'clock, Skyrim-style.
            this.pickAngle = 360;
            this._tensionCreaked = false;
            if (hint) {
                hint.textContent =
                    'Snap! Pick broke — ' +
                    (this.picksRemaining > 0 ? this.picksRemaining + ' left. ' : '') +
                    'A fresh pick is set at the top.';
                hint.classList.add('text-warning');
            }
        } catch (ex) {
            // The server may have consumed a pick (or ended the session) without us hearing
            // back — continuing blind desyncs the pick count and can double-spend. End cleanly;
            // re-opening the lock starts a fresh, consistent session. Best-effort server
            // teardown too, or the lockpicking status + token would dangle until their timers:
            // cancel tolerates both a dead token (session_expired) and a live one.
            console.error(ex);
            lpCancel(this.token, 'break_request_failed', true);
            this.endSession('Connection hiccup — lockpicking ended. Open the lock again to retry.');
        } finally {
            this.breaking = false;
            if (!this.finished && btnExit) btnExit.disabled = false;
        }
    }

    /** Terminal state: freeze input, message, then refresh the panes (server already cleaned up). */
    endSession(message: string) {
        this.finished = true;
        this.sfx.strain(false, 0);
        const hint = this.wrapEl.querySelector('#lockpick_hint');
        if (hint) {
            hint.textContent = message;
            hint.classList.add('text-warning');
        }
        setTimeout(() => {
            this.sfx.dispose();
            // Sampled BEFORE releasing: a delayed teardown from a SUPERSEDED game must not refresh
            // or close the drawer — a newer minigame is live in there, and closing it would detach
            // its canvas and cancel THAT attempt (the very bug this guard exists for).
            const owned = ownsSideInfo(this);
            // Release first: refreshSidePanel() is one of the writers that skips a claimed drawer.
            releaseSideInfoForMinigame(this);
            if (!owned) return;
            if (typeof loadPostFunctions === 'function') loadPostFunctions();
            if (typeof refreshSidePanel === 'function') refreshSidePanel();
            if (typeof closeSideInfoPanel === 'function') closeSideInfoPanel();
        }, 1400);
    }

    async exitMinigame() {
        if (this.submitting || this.finished || this.breaking || this.exiting) return;
        // Exit is a real state transition: freeze physics NOW, or held torque can reach full
        // turn and auto-submit (or the strain timer can snap a pick) while cancel is in flight.
        this.exiting = true;
        this.releaseDrags();
        this.torqueInput = 0;
        this.strainTime = 0;
        this.sfx.strain(false, 0);
        const btnExit = this.wrapEl.querySelector('#lockpick_btn_exit') as HTMLButtonElement | null;
        if (btnExit) btnExit.disabled = true;

        /**
         * ⛔ SNAP ON EXIT (2026-08-03) — leaving with a damaged pick costs you the pick.
         *
         * Without this, wear is trivially laundered: strain a pick nearly to death, back out, walk
         * straight back in, and the client object is rebuilt with pickWear = 0 for the price of the
         * re-entry AP. That reopens the exact free-probing hole wear exists to close.
         *
         * The fiction is also the mechanic: you cannot withdraw a pick that is under tension gently
         * — you yank it and it snaps. Because damage can never leave the attempt, NOTHING has to be
         * persisted server-side; that is why this beats carrying wear across sessions.
         *
         * ⚠ THIS RUNS ONLY ON A DELIBERATE EXIT. The minigame is also torn down by events the player
         * never chose — loop()'s orphan branch fires reason='panel_replaced' when a websocket
         * repaint detaches the canvas (the 2026-07-24 self-cancel bug, still logged because it can
         * recur), plus 'init_host_gone', 'mount_failed', 'break_request_failed' and
         * 'submit_request_failed'. None of those call this method, and none of them may ever cost a
         * pick — destroying real inventory on a dropped request would be indefensible. Do not
         * "helpfully" hoist this into the teardown path in loop().
         *
         * ⚠ Gated on picksConsumable: a lock that does not list Lockpicking Tools never spends one
         * (handleBreak's slip branch), so exiting it must be free too.
         *
         * Reuses lockpick_break_inc.php rather than teaching cancel to consume — that endpoint is
         * already the single authority on spending a pick, logging it, and ending the session when
         * the last one goes. No server change was needed for any of this.
         */
        const snapOnExit = this.picksConsumable && this.pickWear > 0.0001;
        this.pickWear = 0;
        // Set once the break request has actually gone out, so the cancel below can tell a
        // token the SERVER killed (expected — the snap took the last pick) from one that expired
        // under us (a real fault worth reporting).
        let snapRequested = false;
        if (snapOnExit) {
            // The sound fires immediately — it is feedback for the gesture, not a claim about
            // server state. The TEXT deliberately waits for confirmation below: announcing "it
            // snaps" up front would be a lie whenever the request fails, and a player who was told
            // they lost a pick they still have has been misinformed about their own inventory.
            this.sfx.snap();
            try {
                snapRequested = true;
                // ⚠ Without a deadline a hung break request means cancel is NEVER sent: the Exit
                // button is already disabled, so the player is stuck staring at a dead panel
                // while the attempt stays live server-side until its own timer. Bounded here so
                // exit always completes; a slow break just loses the consumption, which errs
                // toward the player.
                const bj = window.ftLockpickBreak
                    ? await Promise.race([window.ftLockpickBreak(this.token), new Promise<null>(r => setTimeout(() => r(null), 4000))])
                    : null;
                if (bj && bj.ok) {
                    const hint = this.wrapEl.querySelector('#lockpick_hint');
                    if (hint) {
                        hint.textContent = 'You pull the pick free under tension and it snaps.';
                        hint.classList.add('text-warning');
                    }
                    // That pick was the last one: the server has already cleared the status, logged
                    // the failure and invalidated the token. Cancelling now would only answer
                    // session_expired and surface a misleading "Session expired." on a deliberate exit.
                    if (bj.sessionOver) {
                        this.finished = true;
                        this.sfx.dispose();
                        const ownedNow = ownsSideInfo(this);
                        releaseSideInfoForMinigame(this);
                        if (!ownedNow) return;
                        if (typeof loadPostFunctions === 'function') loadPostFunctions();
                        if (typeof refreshSidePanel === 'function') refreshSidePanel();
                        if (typeof closeSideInfoPanel === 'function') closeSideInfoPanel();
                        return;
                    }
                }
            } catch (ex) {
                // Non-fatal — timeout, abort, or a dropped response. The exit itself still has to
                // complete. Worst case the pick survives, which errs toward the player and never
                // strands the attempt.
                console.error(ex);
            }
        }

        try {
            const out = window.ftLockpickCancel ? await window.ftLockpickCancel(this.token, 'user_exit') : { ok: false, error: 'unavailable' };
            // An attempt that already ended (the snap took the last pick, the timer expired) answers ok with noop.
            const j = out.ok ? { ok: true } : { ok: false, error: out.error };
            if (!j.ok) {
                if (j.error === 'attempt_expired') {
                    // Token already dead server-side — tear down instead of stranding a
                    // live-looking panel behind a permanently failing Exit button.
                    //
                    // When we just requested a snap, an expired token is the EXPECTED outcome of
                    // that snap taking the caster's last pick: the server ends the session and
                    // invalidates the token, and the response telling us so can be lost in transit.
                    // Reporting "Session expired." there blames a timeout for a thing the player
                    // deliberately did, so word it for what actually happened.
                    this.endSession(snapRequested
                        ? 'Your pick snaps as you pull it free — and that was your last one.'
                        : 'Session expired.');
                    return;
                }
                this.exiting = false;
                if (btnExit) btnExit.disabled = false;
                const errEl = this.wrapEl.querySelector('#lockpick_err') as HTMLElement | null;
                if (errEl) {
                    errEl.textContent = j.error || 'Could not exit';
                    errEl.style.display = 'block';
                }
                return;
            }
        } catch (ex) {
            console.error(ex);
            this.exiting = false;
            if (btnExit) btnExit.disabled = false;
            return;
        }

        this.finished = true;
        this.sfx.dispose();
        const owned = ownsSideInfo(this);
        releaseSideInfoForMinigame(this);
        if (!owned) return; // superseded — the newer minigame owns the drawer, leave it alone
        if (typeof loadPostFunctions === 'function') loadPostFunctions();
        if (typeof refreshSidePanel === 'function') refreshSidePanel();
        if (typeof closeSideInfoPanel === 'function') closeSideInfoPanel();
    }

    async submitAttempt() {
        this.submitting = true;
        const errEl = this.wrapEl.querySelector('#lockpick_err') as HTMLElement | null;
        if (errEl) {
            errEl.style.display = 'none';
            errEl.textContent = '';
        }
        try {
            // The verdict is judged in the browser against the zone frozen at begin; the server re-derives that zone and
            // commits (minigame_truth_commit_inc.php `lockpick_result`). no_strain = the perfect-play half the server cannot
            // observe (it never sees pointer pressure) — the same trust ceiling the angle itself sits at; the server ANDs it
            // with the break count it holds before awarding anything.
            const out = window.ftLockpickResult ? await window.ftLockpickResult(this.token, this.pickAngle, !this.everStrained) : null;
            if (!out) throw new Error('lockpick result adapter unavailable');
            const j = out.ok ? { ok: true, ...out.body } as Record<string, unknown> : { ok: false, error: out.error };
            if (!j.ok) {
                let em = String(j.error || 'Failed');
                if (em === 'no_lockpicking_tools') {
                    em = 'You no longer have lockpicking tools in your inventory or the encounter.';
                } else if (em === 'lockpicking_tools_out_of_reach') {
                    em = 'You have lockpicking tools on this encounter, but they must be within reach.';
                } else if (em === 'missing_required_tool') {
                    em = 'You are missing another required tool listed on this lock (inventory or encounter).';
                } else if (em === 'attempt_expired') {
                    em = 'The lockpicking attempt timed out.';
                } else if (em === 'not_locked') {
                    em = 'It is no longer locked.';
                }
                if (errEl) {
                    errEl.textContent = em;
                    errEl.style.display = 'block';
                }
                // The result call consumed the session token either way — freeze and refresh,
                // or the loop would re-submit against a dead session every frame.
                this.endSession(em);
                return;
            }
            if (j.success) {
                this.finished = true;
                this.cylinderTurn = 1.35;
                this.sfx.unlock();
                const hint = this.wrapEl.querySelector('#lockpick_hint');
                // `perfect` is the SERVER's verdict, never this client's belief about its own run —
                // it already ANDed our no_strain report with the break count it owns, and the
                // ascension roll (with its public log rows) has already happened by the time we
                // read this. Saying it here just tells the player WHY the log lit up.
                if (hint) hint.textContent = j.perfect ? 'Open — and you never once jammed it.' : 'Unlocked!';
                if (hint) {
                    hint.classList.remove('text-warning');
                    hint.classList.add('text-success');
                }
                setTimeout(() => {
                    this.sfx.dispose();
                    const owned = ownsSideInfo(this);
                    releaseSideInfoForMinigame(this);
                    if (!owned) return; // superseded — don't tear down a newer minigame's drawer
                    if (typeof loadPostFunctions === 'function') loadPostFunctions();
                    if (typeof refreshSidePanel === 'function') refreshSidePanel();
                    if (typeof closeSideInfoPanel === 'function') closeSideInfoPanel();
                    // A beat longer on a flawless pick — the line above is the only place the
                    // player is told why an ascension roll just appeared in the log.
                }, j.perfect ? 1500 : 900);
            } else {
                // Server-side reject at full turn = client/server zone desync (or tampering).
                // The server already ended the session (status cleared, pick possibly broken).
                if (j.pick_broke) this.sfx.snap();
                this.endSession(
                    j.pick_broke
                        ? 'The lock seizes and the pick snaps.'
                        : 'The lock seizes — the attempt fails.',
                );
                setTimeout(() => {
                    // Only while WE still hold the drawer: endSession above keeps the claim until
                    // its own 1400ms teardown (so the "lock seizes" message survives), and if a
                    // newer minigame has taken the drawer this refresh isn't ours to run.
                    if (!ownsSideInfo(this)) return;
                    if (typeof loadPostFunctions === 'function') loadPostFunctions();
                    if (typeof refreshSidePanel === 'function') refreshSidePanel();
                }, 400);
                this.submitting = false;
            }
        } catch (ex) {
            // No retry: cylinderTurn is still at full turn, so clearing `submitting` here would
            // re-submit every animation frame — hammering a token the server may have already
            // consumed. Best-effort cancel (tolerates dead AND live tokens), then end cleanly.
            console.error(ex);
            lpCancel(this.token, 'submit_request_failed', true);
            this.endSession('Connection hiccup — lockpicking ended. Open the lock again to retry.');
        }
    }

    drawFace() {
        const ctx = this.ctx;
        ctx.save();
        ctx.translate(this.cx, this.cy);
        ctx.rotate(this.knobRad());

        if (this.integrated) {
            ctx.fillStyle = '#3d3530';
            const w = this.faceR * 2.1;
            const h = this.faceR * 2.4;
            const r = 8;
            const x0 = -w / 2;
            const y0 = -h / 2;
            ctx.beginPath();
            ctx.moveTo(x0 + r, y0);
            ctx.lineTo(x0 + w - r, y0);
            ctx.quadraticCurveTo(x0 + w, y0, x0 + w, y0 + r);
            ctx.lineTo(x0 + w, y0 + h - r);
            ctx.quadraticCurveTo(x0 + w, y0 + h, x0 + w - r, y0 + h);
            ctx.lineTo(x0 + r, y0 + h);
            ctx.quadraticCurveTo(x0, y0 + h, x0, y0 + h - r);
            ctx.lineTo(x0, y0 + r);
            ctx.quadraticCurveTo(x0, y0, x0 + r, y0);
            ctx.closePath();
            ctx.fill();
            ctx.strokeStyle = '#8a7a60';
            ctx.lineWidth = 3;
            ctx.stroke();
        } else {
            const g = ctx.createRadialGradient(0, 0, 4, 0, 0, this.faceR * 1.1);
            g.addColorStop(0, '#c9a227');
            g.addColorStop(1, '#5c4a20');
            ctx.beginPath();
            ctx.arc(0, 0, this.faceR, 0, Math.PI * 2);
            ctx.fillStyle = g;
            ctx.fill();
            ctx.strokeStyle = '#2a2218';
            ctx.lineWidth = 4;
            ctx.stroke();
        }

        // Strain warning ring (red, ramps with the break timer) beats a "you're close" glow —
        // closeness is already communicated honestly by how far the cylinder turns.
        const strainFrac = this.breakSeconds > 0 ? Math.min(1, this.strainTime / this.breakSeconds) : 0;
        if (strainFrac > 0.02) {
            ctx.strokeStyle = 'rgba(255, ' + Math.round(190 - strainFrac * 140) + ', 90, ' + (0.12 + strainFrac * 0.5) + ')';
            ctx.lineWidth = 2.5 + strainFrac * 5;
            ctx.beginPath();
            ctx.arc(0, 0, this.faceR * 1.04, 0, Math.PI * 2);
            ctx.stroke();
        } else if (this.cylinderTurn > 0.05) {
            const g = Math.min(1, this.cylinderTurn);
            ctx.strokeStyle = 'rgba(255, 220, 140, ' + (0.06 + g * 0.3) + ')';
            ctx.lineWidth = 2 + g * 4;
            ctx.beginPath();
            ctx.arc(0, 0, this.faceR * 1.04, 0, Math.PI * 2);
            ctx.stroke();
        }

        this.drawKeyholeShape(ctx);

        this.drawKnifeLocal(ctx);
        this.drawLockpickLocal(ctx);

        ctx.restore();
    }

    drawKeyholeShape(ctx: CanvasRenderingContext2D) {
        const g = this.getStemGeometry();
        const { bowR, stemW, stemH, bowCy, mergeY } = g;

        ctx.fillStyle = '#1a1510';
        ctx.beginPath();
        ctx.arc(0, bowCy, bowR, 0, Math.PI * 2);
        ctx.fill();

        ctx.beginPath();
        if (typeof ctx.roundRect === 'function') {
            ctx.roundRect(-stemW / 2, mergeY, stemW, stemH, stemW * 0.2);
        } else {
            const r = stemW * 0.2;
            const x = -stemW / 2;
            const y = mergeY;
            const w = stemW;
            const h = stemH;
            ctx.moveTo(x + r, y);
            ctx.lineTo(x + w - r, y);
            ctx.quadraticCurveTo(x + w, y, x + w, y + r);
            ctx.lineTo(x + w, y + h - r);
            ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
            ctx.lineTo(x + r, y + h);
            ctx.quadraticCurveTo(x, y + h, x, y + h - r);
            ctx.lineTo(x, y + r);
            ctx.quadraticCurveTo(x, y, x + r, y);
            ctx.closePath();
        }
        ctx.fill();

        ctx.strokeStyle = 'rgba(0,0,0,0.35)';
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.arc(0, bowCy, bowR * 0.98, 0, Math.PI * 2);
        ctx.stroke();
    }

    /**
     * Tension knife in stem: top sits in lower keyhole slot, body extends onto brass (always visible).
     * ctx: already translate(cx,cy) + rotate(knob); face-local coords.
     */
    drawKnifeLocal(ctx: CanvasRenderingContext2D) {
        const a = this.introKnifeAlpha();
        if (a <= 0.005) return;

        const g = this.getStemGeometry();
        const y0 = this.knifeTopLocalY;
        const guardY = this.knifeGuardLocalY;
        const y1 = this.knifeBottomLocalY;

        const bladeTopHalf = g.stemW * 0.5;      // wedged into the keyhole slot
        const bladeGuardHalf = g.stemW * 0.85;   // widens toward the guard
        const guardHalfW = this.faceR * 0.16;
        const guardH = this.faceR * 0.055;
        const gripHalf = g.stemW * 0.62;
        const pommelR = this.faceR * 0.058;

        ctx.save();
        ctx.globalAlpha *= a;
        let pop = 0.9 + 0.1 * Math.min(1, a * 1.15);
        if (this.knifeHoverActive && !this.draggingTorque) {
            pop *= 1 + 0.06 * Math.sin(performance.now() / 200);
        }
        const mid = (y0 + y1) / 2;
        ctx.translate(0, mid);
        ctx.scale(pop, pop);
        ctx.translate(0, -mid);

        ctx.lineJoin = 'round';

        // ── baked sprite: blade tip on the keyhole, grip hanging out past the face ──
        // Anchored to the same y0..y1 span the vector art below uses, so the hitbox in
        // knifeSegment() keeps describing exactly what is drawn. TIP_FRAC backs out the bake's
        // transparent margin so the steel tip, not the padding, lands in the keyhole.
        if (spriteReady(lockpickDaggerSprite)) {
            const dh = y1 - y0;
            const dw = dh * (lockpickDaggerSprite.naturalWidth / lockpickDaggerSprite.naturalHeight);
            ctx.shadowColor = 'rgba(0,0,0,0.55)';
            ctx.shadowBlur = 6;
            ctx.shadowOffsetY = 2;
            ctx.drawImage(lockpickDaggerSprite, -dw / 2, y0 - dh * LOCKPICK_TOOL_TIP_FRAC, dw, dh);
            ctx.shadowBlur = 0;
            ctx.shadowOffsetY = 0;
            if (this.knifeHoverActive && !this.draggingTorque) {
                ctx.strokeStyle = 'rgba(255, 240, 200, 0.6)';
                ctx.lineWidth = 1.6;
                ctx.beginPath();
                if (typeof ctx.roundRect === 'function') {
                    ctx.roundRect(-guardHalfW, y0, guardHalfW * 2, y1 - y0, 5);
                } else {
                    ctx.rect(-guardHalfW, y0, guardHalfW * 2, y1 - y0);
                }
                ctx.stroke();
            }
            ctx.restore();
            return;
        }

        // ── vector fallback, used until the sprite decodes (or if it 404s) ──
        // ── blade: tapered, keyhole slot → guard ──
        ctx.beginPath();
        ctx.moveTo(-bladeTopHalf, y0);
        ctx.lineTo(bladeTopHalf, y0);
        ctx.lineTo(bladeGuardHalf, guardY);
        ctx.lineTo(-bladeGuardHalf, guardY);
        ctx.closePath();
        ctx.fillStyle = '#c4c8ce';
        ctx.fill();
        ctx.strokeStyle = '#4a4e55';
        ctx.lineWidth = 1.25;
        ctx.stroke();
        // fuller / light catch down the left of the blade
        ctx.fillStyle = 'rgba(255,255,255,0.26)';
        ctx.beginPath();
        ctx.moveTo(-bladeTopHalf * 0.45, y0 + 2);
        ctx.lineTo(0, y0 + 2);
        ctx.lineTo(0, guardY - 2);
        ctx.lineTo(-bladeGuardHalf * 0.45, guardY - 2);
        ctx.closePath();
        ctx.fill();

        // ── crossguard ──
        ctx.fillStyle = '#9a8248';
        ctx.strokeStyle = '#4a3d20';
        ctx.lineWidth = 1.1;
        ctx.beginPath();
        if (typeof ctx.roundRect === 'function') {
            ctx.roundRect(-guardHalfW, guardY, guardHalfW * 2, guardH, guardH * 0.45);
        } else {
            ctx.rect(-guardHalfW, guardY, guardHalfW * 2, guardH);
        }
        ctx.fill();
        ctx.stroke();

        // ── grip: leather-wrapped, runs out PAST the lock face (the part you grab) ──
        const gripTop = guardY + guardH;
        const gripBottom = y1 - pommelR;
        ctx.fillStyle = '#4a3728';
        ctx.strokeStyle = '#241a12';
        ctx.lineWidth = 1.1;
        ctx.beginPath();
        if (typeof ctx.roundRect === 'function') {
            ctx.roundRect(-gripHalf, gripTop, gripHalf * 2, gripBottom - gripTop, gripHalf * 0.5);
        } else {
            ctx.rect(-gripHalf, gripTop, gripHalf * 2, gripBottom - gripTop);
        }
        ctx.fill();
        ctx.stroke();
        // wrap bands — they make the twist read as rotation while the cylinder turns
        ctx.strokeStyle = 'rgba(0,0,0,0.38)';
        ctx.lineWidth = 1;
        const bands = 4;
        for (let i = 1; i <= bands; i++) {
            const by = gripTop + ((gripBottom - gripTop) * i) / (bands + 1);
            ctx.beginPath();
            ctx.moveTo(-gripHalf, by);
            ctx.lineTo(gripHalf, by);
            ctx.stroke();
        }

        // ── pommel ──
        ctx.fillStyle = '#9a8248';
        ctx.strokeStyle = '#4a3d20';
        ctx.lineWidth = 1.1;
        ctx.beginPath();
        ctx.arc(0, gripBottom, pommelR, 0, Math.PI * 2);
        ctx.fill();
        ctx.stroke();

        if (this.knifeHoverActive && !this.draggingTorque) {
            ctx.strokeStyle = 'rgba(255, 240, 200, 0.6)';
            ctx.lineWidth = 1.6;
            ctx.beginPath();
            if (typeof ctx.roundRect === 'function') {
                ctx.roundRect(-guardHalfW, y0, guardHalfW * 2, y1 - y0, 5);
            } else {
                ctx.rect(-guardHalfW, y0, guardHalfW * 2, y1 - y0);
            }
            ctx.stroke();
        }

        ctx.restore();
    }

    /**
     * Lockpick in upper keyhole (bow): pivots here to aim anywhere on the ~300° reachable arc.
     * ctx: face-local, rotated with knob.
     */
    drawLockpickLocal(ctx: CanvasRenderingContext2D) {
        const a = this.introPickAlpha();
        if (a <= 0.005) return;

        const wob = this.vibration * 0.03 * (Math.random() - 0.5);
        ctx.save();
        ctx.globalAlpha *= a;
        const pop = 0.92 + 0.08 * Math.min(1, a * 1.15);
        ctx.translate(0, this.lockpickPivotLocalY);
        ctx.scale(pop, pop);
        // pickAngle is an extended clock angle (0 = 12 o'clock, CW+, plus whole turns) and the
        // blade is drawn pointing up, so the rotation IS pickAngle — rotation being periodic,
        // the extra turn drops out. See pickSegment() for why the old `- 90` was the core bug.
        ctx.rotate((this.pickAngle * Math.PI) / 180 + wob);

        // Baked sprite: hooked tip out at the blade end, wrapped handle trailing past the pivot.
        // 1.42x pickBladeLen so the handle has somewhere to sit; the HOOK still lands exactly on
        // -pickBladeLen, which is the end pickSegment() hit-tests to.
        if (spriteReady(lockpickPickSprite)) {
            const ph = this.pickBladeLen * 1.42;
            const pw = ph * (lockpickPickSprite.naturalWidth / lockpickPickSprite.naturalHeight);
            ctx.shadowColor = 'rgba(0,0,0,0.5)';
            ctx.shadowBlur = 5;
            ctx.shadowOffsetY = 1;
            ctx.drawImage(lockpickPickSprite, -pw / 2, -this.pickBladeLen - ph * LOCKPICK_TOOL_TIP_FRAC, pw, ph);
            ctx.restore();
            return;
        }

        // ── vector fallback, used until the sprite decodes (or if it 404s) ──
        ctx.lineCap = 'round';
        ctx.lineJoin = 'round';
        ctx.strokeStyle = '#f0f2f6';
        ctx.lineWidth = 2.4;
        ctx.shadowColor = 'rgba(0,0,0,0.55)';
        ctx.shadowBlur = 4;
        ctx.shadowOffsetY = 1;
        ctx.beginPath();
        ctx.moveTo(0, 8);
        ctx.lineTo(0, -this.pickBladeLen);
        ctx.stroke();
        ctx.shadowBlur = 0;
        ctx.shadowOffsetY = 0;

        ctx.strokeStyle = '#7a8088';
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.moveTo(0, 8);
        ctx.lineTo(0, -this.pickBladeLen);
        ctx.stroke();

        ctx.restore();
    }

    drawHud() {
        const ctx = this.ctx;
        ctx.fillStyle = 'rgba(0,0,0,0.45)';
        ctx.fillRect(0, this.h - 34, this.w, 34);

        // Pick supply, Skyrim-style: a row of little pick glyphs (capped, then "×N").
        if (this.picksConsumable) {
            ctx.save();
            ctx.strokeStyle = '#e8eaef';
            ctx.lineWidth = 2;
            ctx.lineCap = 'round';
            const shown = Math.min(8, this.picksRemaining);
            for (let i = 0; i < shown; i++) {
                const x = 14 + i * 12;
                ctx.beginPath();
                ctx.moveTo(x, this.h - 8);
                ctx.lineTo(x + 4, this.h - 26);
                ctx.stroke();
            }
            ctx.fillStyle = '#ddd';
            ctx.font = '11px sans-serif';
            if (this.picksRemaining > 8) {
                ctx.fillText('×' + this.picksRemaining, 14 + shown * 12 + 4, this.h - 12);
            } else if (this.picksRemaining === 0) {
                ctx.fillText('No picks!', 14, this.h - 12);
            }
            ctx.restore();
        }

        // Strain meter: red fill racing right while the pick is tortured, over a DARKER stub for
        // permanent wear that the live strain can never fall below. The stub is what makes "this
        // pick is damaged" legible at a glance — without it, wear would silently change how soon
        // the next probe snaps and the player would have no way to see why.
        const strainFrac = this.breakSeconds > 0 ? Math.min(1, this.strainTime / this.breakSeconds) : 0;
        const wearFrac = this.breakSeconds > 0 ? Math.min(1, this.pickWear / this.breakSeconds) : 0;
        if (strainFrac > 0.02 || wearFrac > 0.005) {
            const bw = this.w * 0.4;
            const bx = this.w - bw - 12;
            const by = this.h - 22;
            this.ctx.fillStyle = 'rgba(255,255,255,0.16)';
            this.ctx.fillRect(bx, by, bw, 8);
            if (wearFrac > 0) {
                this.ctx.fillStyle = 'rgba(150,40,40,0.95)';
                this.ctx.fillRect(bx, by, bw * wearFrac, 8);
            }
            if (strainFrac > wearFrac) {
                this.ctx.fillStyle = 'rgba(255,' + Math.round(170 - strainFrac * 130) + ',70,0.9)';
                this.ctx.fillRect(bx + bw * wearFrac, by, bw * (strainFrac - wearFrac), 8);
            }
        }
    }

    loop() {
        if (!this.canvas.isConnected) {
            this.sfx.dispose();
            releaseSideInfoForMinigame(this);
            window.removeEventListener('blur', this._onWindowBlur);
            // Canvas gone WITHOUT a deliberate end (unlock/exit/endSession all set `finished`
            // before the panel repaints) = the panel was replaced under us — orphaned attempt.
            // Best-effort teardown so the token + lockpicking status don't dangle.
            if (!this.finished) {
                // 'panel_replaced' is the signature of the self-cancel bug: something repainted
                // #div_refresh_side_info under a live attempt. It is logged server-side so a
                // recurrence names itself instead of needing a re-hunt.
                lpCancel(this.token, 'panel_replaced', true);
            }
            return;
        }
        const now = performance.now();
        const dt = this._lastFrameTs ? Math.min(0.05, (now - this._lastFrameTs) / 1000) : 0.016;
        this._lastFrameTs = now;

        const maxTurn = this.maxTurnForPick();

        if (!this.draggingTorque && !this.finished && !this.submitting) {
            // Spring-back: released tension unwinds the cylinder quickly.
            this.torqueInput = Math.max(0, this.torqueInput - dt * 2.6);
        }

        if (!this.finished && !this.submitting && !this.breaking && !this.exiting) {
            const effective = Math.min(this.torqueInput, maxTurn);
            this.cylinderTurn += (effective - this.cylinderTurn) * Math.min(1, dt * 14);

            // Jam + strain: holding torque past what the pick position allows.
            const overpush = this.draggingTorque ? Math.max(0, this.torqueInput - maxTurn) : 0;
            // ⛔ The jam tolerance SCALES with how far the cylinder can actually go (2026-07-30). A
            // flat 0.03 left a free scan alive even after the difficulty rework: in the dead region
            // maxTurn is 0, so a player could micro-drag the dagger to ~0.03 — below the threshold,
            // therefore no strain, no pick risk — and still read "did the cylinder budge at all?" at
            // every position on the ring. That is the same zero-cost rangefinder the rework exists to
            // kill, just walked round the face one step at a time. A lock that gives you NOTHING is
            // being forced the moment you lean on it, so tolerance floors near zero there and only
            // relaxes back to ~0.03 where the cylinder genuinely has room to move (and inside the
            // sweet spot maxTurn is 1, where `straining` is false regardless — feel is untouched).
            const jamTolerance = 0.005 + 0.025 * maxTurn;
            const straining = overpush > jamTolerance && maxTurn < 1;
            if (straining) {
                // Sticky for the whole session — see the field's note. Set the moment the jam
                // begins, not at the snap: probing the ring by leaning on the dagger and easing off
                // before it breaks is exactly the play that forfeits the flawless pick.
                this.everStrained = true;
                if (!this._jamKnocked) {
                    this._jamKnocked = true;
                    this.sfx.jam();
                }
                // Farther from the sweet spot = the pick suffers faster (Skyrim's steeper struggle).
                const bite = 1 + (1 - maxTurn) * 0.5;
                this.strainTime += dt * bite;
                // Permanent damage accrues on the same clock, so a cold probe also WEARS faster.
                // Capped at breakSeconds: at the cap strainTime can no longer fall below the
                // threshold, so the next frame snaps the pick — a fully worn pick is a broken one.
                this.pickWear = Math.min(this.breakSeconds, this.pickWear + dt * LOCKPICK_WEAR_RATE * bite);
                const strainFrac = this.breakSeconds > 0 ? Math.min(1, this.strainTime / this.breakSeconds) : 1;
                this.vibration = 0.5 + overpush * 2.2 + strainFrac * 1.1;
                this.sfx.strain(true, strainFrac);
                if (this.strainTime >= this.breakSeconds) {
                    void this.handleBreak();
                }
            } else {
                if (overpush <= 0.005) this._jamKnocked = false;
                // Relief is real but never complete — the meter falls back to the wear floor, not
                // to zero. This single `Math.max` IS the anti-probing fix; reverting it to 0
                // restores the free rangefinder described on LOCKPICK_WEAR_RATE.
                this.strainTime = Math.max(this.pickWear, this.strainTime - dt * 1.6);
                this.vibration = Math.max(0, this.vibration * 0.82 - 0.02);
                this.sfx.strain(false, 0);
            }

            // Full turn only happens inside the sweet spot → attempt (server re-validates).
            if (maxTurn >= 1 && this.cylinderTurn >= 0.96) {
                this.cylinderTurn = 1;
                void this.submitAttempt();
            }
        } else {
            this.vibration = Math.max(0, this.vibration * 0.82 - 0.02);
        }

        this._frameKnobJitter = this.vibration * 0.04 * (Math.random() - 0.5);
        const ctx = this.ctx;
        // Re-assert the HiDPI base transform every frame: drawFace() nests save/translate/rotate
        // /restore, and setTransform (not scale) means a stray unbalanced restore in the draw code
        // can never let the scale factor accumulate across frames.
        ctx.setTransform(this.dpr, 0, 0, this.dpr, 0, 0);
        ctx.fillStyle = '#1e1e22';
        ctx.fillRect(0, 0, this.w, this.h);
        this.drawFace();
        this.drawHud();
        requestAnimationFrame(this._loop);
    }
}

/** Double-click / double-tap dedupe: one init request at a time (server also reuses an
 *  active attempt, but the blob-recompile lag makes rapid re-inits invisible to it). */
let lockpickOpenInFlight = false;

async function openLockpickMinigame(lockInventoryId: string): Promise<void> {
    if (!lockInventoryId) return;
    if (lockpickOpenInFlight) return;
    lockpickOpenInFlight = true;
    // Set once the server has minted an attempt, cleared once a minigame is mounted to own it.
    // Non-empty in the catch = the attempt exists server-side with nothing to end it → cancel.
    let mintedToken: unknown = null;
    try {
        if (typeof forceOpenSideInfoPanel === 'function') forceOpenSideInfoPanel();
        const host = document.getElementById('div_refresh_side_info');
        if (host) {
            host.innerHTML =
                '<div class="p-3 text-center"><div class="spinner-border spinner-border-sm"></div><p class="small mt-2 mb-0">Starting lockpick…</p></div>';
        }

        const { roll_private, encounter_id, caster_id } = lockpickSidelogContext();

        // CLIENT TRUTH (L5 S2): the zone, feel halo, struggle time, tools gate and the pick supply are computed here from the
        // rows this page holds; ONE begin commit spends the AP, applies the status and mints the sweet spot (first writer wins).
        const begun = window.ftLockpickBegin
            ? await window.ftLockpickBegin({ encounterId: encounter_id, actorId: caster_id, lockInventoryId, rollPrivate: roll_private })
            : null;
        if (!begun || begun.outcome !== 'handled') {
            if (host) {
                const msg = begun ? begun.message : 'Select a character in this encounter first.';
                host.innerHTML =
                    '<div class="p-3"><p class="text-danger mb-0">Cannot start lockpicking: ' +
                    escapeHtml(msg) +
                    '</p></div>';
            }
            return;
        }
        const data = begun.config as unknown as LockpickConfig & { lock_name?: string };
        mintedToken = begun.session;

        // Re-grab the host: the panel can be replaced/removed while init ran. The server-side
        // attempt + token are already minted — tear the token down so nothing dangles.
        const hostNow = document.getElementById('div_refresh_side_info');
        if (!hostNow || !hostNow.isConnected) {
            lpCancel(data.token, 'init_host_gone', true);
            return;
        }
        hostNow.innerHTML = '';
        const wrap = document.createElement('div');
        wrap.className = 'lockpick-minigame-wrap p-2';
        wrap.innerHTML =
            '<div class="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-2">' +
            '<h4 class="h6 mb-0">Lockpicking</h4>' +
            '<button type="button" class="btn btn-sm btn-outline-secondary" id="lockpick_btn_exit">Exit</button>' +
            '</div>' +
            '<p class="small text-muted mb-2">' +
            escapeHtml(data.lock_name || 'Lock') +
            '</p>' +
            '<canvas class="lockpick-canvas d-block mx-auto border rounded shadow-sm" width="320" height="380" tabindex="0"></canvas>' +
            '<div class="d-flex flex-wrap gap-2 mt-2 align-items-center">' +
            '<span class="small text-muted flex-grow-1" id="lockpick_hint">Drag the <strong>pick</strong> to set it in the lock, then twist the <strong>dagger</strong> to turn the cylinder. The further it turns before it jams, the closer your pick is — but straining a jammed lock snaps picks. A full turn opens it.</span>' +
            '</div>' +
            '<div class="mt-2 text-danger small" id="lockpick_err" style="display:none;"></div>';

        hostNow.appendChild(wrap);
        const canvas = wrap.querySelector('canvas') as HTMLCanvasElement;
        const game = new FateTideLockpickMinigame(canvas, data, wrap);
        // Claim the drawer for THIS instance. Construction first so a throwing constructor can't
        // leave a claim nobody will ever release (which would silence refreshSidePanel for the
        // rest of the page session); the loop's first frame is a rAF away, so nothing races here.
        // The claim must be in place before the websocket push our OWN init opened (it applies the
        // lockpicking status + spends AP) lands ~1s from now — that push is what used to repaint
        // the canvas away and make the attempt cancel itself.
        claimSideInfoForMinigame(hostNow, game);
        // ── test hook ────────────────────────────────────────────────────────────────────────
        // The live instance, for staging smokes to READ. Not an API and not a control surface —
        // nothing in the app touches it.
        //
        // It exists because the alternative is worse: the e2e smoke used to infer the tools'
        // positions by scanning the canvas for "steel-coloured" pixels (blue channel over a tuned
        // threshold). That only ever worked while the tools were flat vector art on a flat brass
        // disc, and it broke outright the moment the face became the lock's photographic render and
        // the dagger became a baked 3D sprite — the test was doing fragile computer vision to
        // recover numbers the object already knows exactly.
        window.__ftLockpickGame = game;
        mintedToken = null; // mounted — the minigame owns the session's teardown from here
    } catch (e) {
        console.error(e);
        const host = document.getElementById('div_refresh_side_info');
        if (host) host.innerHTML = '<div class="p-3 text-danger">Lockpick failed to load.</div>';
        // The server already minted the attempt (3 AP + the lockpicking status) but no minigame
        // exists to ever end it — without this the token and the status dangle until their timers.
        if (mintedToken) lpCancel(mintedToken, 'mount_failed', true);
    } finally {
        lockpickOpenInFlight = false;
    }
}

/**
 * "Unlock with Keys" / "Lock with Key" (object card buttons). action = 'unlock' (default) opens a
 * locked row; action = 'lock' RELOCKS an unlocked row the caster holds a matching key for.
 *
 * CLIENT TRUTH (Lane L5 S1, 2026-09-26): window.ftTryCommitUseKey (websocket/minigame-truth.ts →
 * janus-engine/minigames/use-key.ts) picks the key off the actor's carried tree (computed.key_links is
 * the display-only hint), checks the lock state and commits to options/game/actions/
 * minigame_truth_commit_inc.php, which authorizes against the inventory_key_links TABLE and patches
 * the caches. A provable refusal (no keys / none fit / not locked / already locked) never sends.
 *
 * lockInventoryId = the row carrying `locked` as caster; displayInventoryId = the row on the
 * card (the physical-lock pattern makes these differ — the server accepts a link to either).
 *
 * UX is INLINE: the spinner + outcome swap into the row's own result span next to the button —
 * the side-info panel is never wholesale repainted. Success is logged server-side and the
 * panels refresh to pick it up.
 */
async function useKeysOnLock(lockInventoryId: string, displayInventoryId: string, action: string = 'unlock', options?: { feedback?: HTMLElement; keyInventoryId?: string }): Promise<void> {
    if (!lockInventoryId) return;
    const relock = action === 'lock';
    const btn = document.getElementById(
        (relock ? 'btn_lock_keys_' : 'btn_use_keys_') + displayInventoryId,
    ) as HTMLButtonElement | null;
    const out = options?.feedback ?? document.getElementById((relock ? 'lock_keys_result_' : 'use_keys_result_') + displayInventoryId);
    const setOut = (html: string, cls: string): void => {
        if (!out) return;
        out.className = 'small ' + cls;
        out.innerHTML = html;
    };
    if (btn) btn.disabled = true;
    setOut('<span class="spinner-border spinner-border-sm me-1" role="status"></span>Trying your keys…', 'text-muted');
    try {
        const caster_id = String(window.ftGetActiveCasterId?.() ?? '').trim();
        const { roll_private, encounter_id } = lockpickSidelogContext();
        if (!caster_id) {
            setOut('Select a character first.', 'text-warning');
            if (btn) btn.disabled = false;
            return;
        }
        // CLIENT TRUTH (L5 S1): the browser picks the key off the actor's own carried tree and checks the lock state; the
        // server authorizes against the inventory_key_links TABLE, CASes the lock and patches the caches — no recompile.
        // options/game/keys/use_key_inc.php is retired; there is no second lane to fall back to.
        const commit = window.ftTryCommitUseKey;
        const outcome = typeof commit === 'function'
            ? await commit({ encounterId: encounter_id, actorId: caster_id, lockInventoryId, displayInventoryId: displayInventoryId || '',
                action: relock ? 'lock' : 'unlock', keyInventoryId: options?.keyInventoryId, rollPrivate: roll_private })
            : null;
        if (!outcome) {
            setOut(relock ? 'Lock with key failed.' : 'Unlock with keys failed.', 'text-danger');
            if (btn) btn.disabled = false;
            return;
        }
        if (outcome.outcome === 'refused') {
            setOut(escapeHtml(outcome.message), 'text-warning');
            if (btn) btn.disabled = false;
            return;
        }

        setOut(
            escapeHtml(outcome.keyName || 'A key')
            + (relock ? ' locks ' : ' opens ')
            + escapeHtml(outcome.lockName || 'the lock') + '!',
            'text-success',
        );
        // The committed lock state is already stamped on every cached copy and the saved roster rows adopted
        // (websocket/minigame-truth.ts) — the repaint below reads the confirmed state, not a stale blob.
        // Leave the button disabled — the row changed state; the refresh repaints the card.
        setTimeout(() => {
            if (typeof loadPostFunctions === 'function') loadPostFunctions();
            if (typeof refreshSidePanel === 'function') refreshSidePanel();
        }, 300);
    } catch (e) {
        console.error(e);
        setOut(relock ? 'Lock with key failed.' : 'Unlock with keys failed.', 'text-danger');
        if (btn) btn.disabled = false;
    }
}

declare global {
    interface Window {
        openLockpickMinigame: typeof openLockpickMinigame;
        useKeysOnLock: typeof useKeysOnLock;
        // Set while a canvas minigame owns #div_refresh_side_info — the automatic side-info
        // repaint paths (object-card/open-side-info.ts, websocket/start-turn-gm.ts,
        // template.js refreshSidePanel) skip a claimed drawer. See claimSideInfoForMinigame.
        __ftSideInfoMinigameActive?: boolean;
        ftClearOpenSideInfo?: () => void;
        /** Live minigame instance — READ-ONLY test hook for staging smokes. See openLockpickMinigame. */
        __ftLockpickGame?: FateTideLockpickMinigame;
    }
}

window.openLockpickMinigame = openLockpickMinigame;
window.useKeysOnLock = useKeysOnLock;

export {};
